/**
 * @component ry-image
 * @category  display
 * @summary   Wrapped `<img>` with shape variants (circular, rounded, avatar) and load/error event normalization (Fomantic UI `.ui.image` equivalent). Mirrors `src` and `alt` to the inner image; re-emits `load` / `error` as `ry:load` / `ry:error`. Supports a [placeholder] background while loading.
 *
 * Reference: Fomantic UI's image element. ry-image adds a small load-state
 * lifecycle so consumers can show skeletons or fallback art without writing
 * their own onload bindings.
 *
 * @attribute src         string?              Image URL. Mirrors to the inner `<img>`. Changing this re-fetches and re-emits load/error.
 * @attribute alt         string?   default=""  Alt text for the inner image. ALWAYS set this for non-decorative images; pass an empty alt explicitly for decorative use.
 * @attribute circular    boolean   default=false   Render as a circle (50% border-radius). Use with square images and avatars.
 * @attribute rounded     boolean   default=false   Render with rounded corners (var(--ry-radius-md)). Mutually exclusive with [circular].
 * @attribute bordered    boolean   default=false   Adds a border around the image.
 * @attribute avatar      boolean   default=false   Shorthand for `[circular]` with a small fixed size — common avatar pattern.
 * @attribute fluid       boolean   default=false   Image stretches to fill its container width.
 * @attribute size        'mini' | 'sm' | 'md' | 'lg' | 'xl'?   Pure CSS hook for preset sizes.
 *
 * @property  loaded      boolean   Read-only — true after `ry:load` has fired (the inner image successfully loaded).
 * @property  errored     boolean   Read-only — true after `ry:error` has fired.
 *
 * @event ry:load
 *        detail: { src: string }
 *        fires:  when the inner image fires its native `load` event. `detail.src` is the URL that loaded.
 *
 * @event ry:error
 *        detail: { src: string }
 *        fires:  when the inner image fires its native `error` event. `detail.src` is the URL that failed.
 *
 * @state     `data-ry-state`: `"loading"` (before load fires), `"loaded"` (after success), `"errored"` (after failure).
 *
 * @content   None — `<ry-image>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-image[src][alt?][circular?][rounded?][bordered?][avatar?][fluid?][size?][data-ry-state]
 *     > img.ry-image__img    [data-ry-target="img"]    src=src   alt=alt
 *
 * @gotcha  Setting `alt=""` (empty string) is VALID — it tells screen readers the image is decorative. Omitting the `alt` attribute entirely is also accepted; ry-image normalizes to empty string. There is no warning in either case (unlike `<ry-button>` icon-only which warns).
 *
 * @gotcha  `[avatar]` is a SHORTHAND — it sets `circular` styling implicitly via CSS and applies a small fixed size. Setting both `[avatar]` and `[rounded]` is contradictory; the CSS cascade decides which wins. Don't mix them.
 *
 * @gotcha  `ry:load` / `ry:error` fire from the inner `<img>`'s native events. In happy-dom (and in unit tests), no real HTTP request happens — to test, dispatch the events manually on the inner img element.
 *
 * @gotcha  Changing `src` after mount resets `data-ry-state` to "loading" and re-arms for load/error. Listeners that fired previously will fire again for the new src.
 *
 * @example basic
 * ```html
 * <ry-image src="https://picsum.photos/seed/cat/240/160" alt="A photo of a cat"></ry-image>
 * ```
 *
 * @example avatar
 * ```html
 * <ry-image avatar src="https://i.pravatar.cc/64?img=11" alt="Profile photo of Jane"></ry-image>
 * ```
 *
 * @example circular-large
 * ```html
 * <ry-image circular size="lg" src="https://i.pravatar.cc/128?img=33" alt=""></ry-image>
 * ```
 *
 * @example rounded-bordered
 * ```html
 * <ry-image rounded bordered src="https://picsum.photos/seed/album/200/200" alt="Album cover"></ry-image>
 * ```
 *
 * @example fluid
 * ```html
 * <ry-image fluid src="https://picsum.photos/seed/hero/1200/300" alt="Hero image"></ry-image>
 * ```
 *
 * @tutorial mount-renders-inner-img
 * ```js
 * const el = mount('<ry-image src="/cat.jpg" alt="A cat"></ry-image>');
 * const img = el.querySelector('[data-ry-target="img"]');
 * await expectEqual(!!img, true, 'inner img exists');
 * await expectAttr(img, 'src', '/cat.jpg');
 * await expectAttr(img, 'alt', 'A cat');
 * ```
 *
 * @tutorial empty-alt-is-passed-through
 * ```js
 * // Wrong assumption: missing alt becomes "image" or some fallback.
 * // It normalizes to empty string (decorative).
 * const el = mount('<ry-image src="/x.jpg"></ry-image>');
 * const img = el.querySelector('[data-ry-target="img"]');
 * await expectAttr(img, 'alt', '', 'empty alt for decorative image');
 * ```
 *
 * @tutorial state-starts-loading
 * ```js
 * const el = mount('<ry-image src="/x.jpg" alt="x"></ry-image>');
 * await expectAttr(el, 'data-ry-state', 'loading');
 * ```
 *
 * @tutorial load-event-transitions-to-loaded
 * ```js
 * const el = mount('<ry-image src="/x.jpg" alt="x"></ry-image>');
 * const img = el.querySelector('[data-ry-target="img"]');
 * const fired = [];
 * el.addEventListener('ry:load', (e) => fired.push(e.detail.src));
 * img.dispatchEvent(new Event('load'));
 * await expectAttr(el, 'data-ry-state', 'loaded');
 * await expectEqual(el.loaded, true);
 * await expectEqual(fired.length, 1, 'ry:load fired');
 * await expectEqual(fired[0], '/x.jpg', 'detail.src reflects current src');
 * ```
 *
 * @tutorial error-event-transitions-to-errored
 * ```js
 * const el = mount('<ry-image src="/bad.jpg" alt="x"></ry-image>');
 * const img = el.querySelector('[data-ry-target="img"]');
 * const fired = [];
 * el.addEventListener('ry:error', (e) => fired.push(e.detail.src));
 * img.dispatchEvent(new Event('error'));
 * await expectAttr(el, 'data-ry-state', 'errored');
 * await expectEqual(el.errored, true);
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0], '/bad.jpg');
 * ```
 *
 * @tutorial src-change-resets-state
 * ```js
 * // Wrong assumption: data-ry-state stays "loaded" forever after a successful
 * // load. Changing src resets the lifecycle.
 * const el = mount('<ry-image src="/x.jpg" alt="x"></ry-image>');
 * const img = el.querySelector('[data-ry-target="img"]');
 * img.dispatchEvent(new Event('load'));
 * await expectAttr(el, 'data-ry-state', 'loaded');
 * el.setAttribute('src', '/y.jpg');
 * await expectAttr(el, 'data-ry-state', 'loading', 'state reset on src change');
 * await expectAttr(el.querySelector('[data-ry-target="img"]'), 'src', '/y.jpg', 'inner img src updated');
 * ```
 *
 * @tutorial attribute-hooks-survive
 * ```js
 * const el = mount('<ry-image src="/x.jpg" alt="" circular bordered fluid size="lg"></ry-image>');
 * await expectEqual(el.hasAttribute('circular'), true);
 * await expectEqual(el.hasAttribute('bordered'), true);
 * await expectEqual(el.hasAttribute('fluid'), true);
 * await expectAttr(el, 'size', 'lg');
 * ```
 *
 * @see ry-card
 * @see ry-placeholder
 */

import { RyElement } from '../core/ry-element.js';

interface ImageEventDetail {
  src: string;
}

export class RyImage extends RyElement {
  static get observedAttributes(): string[] {
    return ['src', 'alt'];
  }

  setup(): void {
    this.#render();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;
    if (name === 'src') {
      const img = this.$<HTMLImageElement>('[data-ry-target="img"]');
      if (img) {
        this.dataset.ryState = 'loading';
        img.setAttribute('src', newValue ?? '');
      }
    } else if (name === 'alt') {
      const img = this.$<HTMLImageElement>('[data-ry-target="img"]');
      if (img) img.setAttribute('alt', newValue ?? '');
    }
  }

  get loaded(): boolean {
    return this.dataset.ryState === 'loaded';
  }

  get errored(): boolean {
    return this.dataset.ryState === 'errored';
  }

  #render(): void {
    if (this.$('[data-ry-target="img"]')) return;
    const src = this.getAttribute('src') ?? '';
    const alt = this.getAttribute('alt') ?? '';
    const img = document.createElement('img');
    img.setAttribute('data-ry-target', 'img');
    img.className = 'ry-image__img';
    img.setAttribute('src', src);
    img.setAttribute('alt', alt);
    this.appendChild(img);
    this.dataset.ryState = 'loading';

    this.on(img, 'load', () => {
      this.dataset.ryState = 'loaded';
      this.emit<ImageEventDetail>('load', { src: this.getAttribute('src') ?? '' });
    });
    this.on(img, 'error', () => {
      this.dataset.ryState = 'errored';
      this.emit<ImageEventDetail>('error', { src: this.getAttribute('src') ?? '' });
    });
  }
}

customElements.define('ry-image', RyImage);
